import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { displayOrganizationNumber } from "@financialharmony/tax-se";
import { Loader2, Mail, Building, Paperclip, Ban, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { UnmatchedEmailsApiClient } from "@/clients/unmatchedEmailsApiClient";
import { CompanyService } from "@/services/companyService";
import { UnmatchedEmail } from "@/types/unmatchedEmails";
import { CompanyOverview } from "@/types/company";
import { useFormatters } from "@financialharmony/ui";

export default function UnmatchedEmails() {
  const t = useTranslations("consultantOnboarding.admin.unmatched");
  const tc = useTranslations("common.actions");
  const { formatDate } = useFormatters();
  const formatReceived = (iso: string) => `${formatDate(iso, "numeric")} ${formatDate(iso, "time")}`;
  const [emails, setEmails] = useState<UnmatchedEmail[]>([]);
  const [companies, setCompanies] = useState<CompanyOverview[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [emailToAssign, setEmailToAssign] = useState<UnmatchedEmail | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [isAssigning, setIsAssigning] = useState(false);

  const [emailToDismiss, setEmailToDismiss] = useState<UnmatchedEmail | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setIsLoading(true);
    try {
      const [fetchedEmails, fetchedCompanies] = await Promise.all([
        UnmatchedEmailsApiClient.list(),
        new CompanyService().getAllCompanies(),
      ]);
      setEmails(fetchedEmails);
      setCompanies(fetchedCompanies);
    } catch {
      toast.error(t("loadFailed"));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAssign() {
    if (!emailToAssign || !selectedCompanyId) return;
    setIsAssigning(true);
    try {
      await UnmatchedEmailsApiClient.assign(emailToAssign.id, selectedCompanyId);
      const company = companies.find((c) => c.id === selectedCompanyId);
      toast.success(t("assigned", { company: company?.name ?? t("fallbackCompany") }));
      setEmails((prev) => prev.filter((e) => e.id !== emailToAssign.id));
      setEmailToAssign(null);
      setSelectedCompanyId("");
    } catch {
      toast.error(t("assignFailed"));
    } finally {
      setIsAssigning(false);
    }
  }

  async function handleDismiss() {
    if (!emailToDismiss) return;
    setIsDismissing(true);
    try {
      await UnmatchedEmailsApiClient.dismiss(emailToDismiss.id);
      toast.success(t("dismissed"));
      setEmails((prev) => prev.filter((e) => e.id !== emailToDismiss.id));
      setEmailToDismiss(null);
    } catch {
      toast.error(t("dismissFailed"));
    } finally {
      setIsDismissing(false);
    }
  }

  return (
    <div className="container max-w-5xl py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t("title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("description")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail size={18} />
            {t("pending")}
            {!isLoading && (
              <Badge variant="secondary" className="ml-1">
                {emails.length}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {t("pendingDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : emails.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
              <Mail size={32} className="opacity-30" />
              <p className="text-sm">{t("empty")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("from")}</TableHead>
                  <TableHead>{t("subject")}</TableHead>
                  <TableHead>{t("received")}</TableHead>
                  <TableHead>{t("attachments")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map((email) => (
                  <TableRow key={email.id}>
                    <TableCell className="font-mono text-xs">{email.sender_email}</TableCell>
                    <TableCell className="max-w-48 truncate text-sm">
                      {email.subject ?? <span className="text-muted-foreground">{t("noSubject")}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatReceived(email.received_at)}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Paperclip size={13} />
                        {email.attachment_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setEmailToAssign(email);
                            setSelectedCompanyId("");
                          }}
                        >
                          <ArrowRight size={14} className="mr-1" />
                          {t("assign")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground"
                          onClick={() => setEmailToDismiss(email)}
                        >
                          <Ban size={14} className="mr-1" />
                          {t("dismiss")}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Assign dialog */}
      <Dialog
        open={!!emailToAssign}
        onOpenChange={(open) => {
          if (!open) {
            setEmailToAssign(null);
            setSelectedCompanyId("");
          }
        }}
      >
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => { if (isAssigning) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (isAssigning) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>{t("assignDialog.title")}</DialogTitle>
            <DialogDescription>
              {t.rich("assignDialog.from", {
                sender: emailToAssign?.sender_email ?? "",
                mono: (chunks) => <span className="font-mono text-xs">{chunks}</span>,
              })}
              <br />
              {emailToAssign?.subject && (
                <>
                  {t("assignDialog.subject", { subject: emailToAssign.subject })}
                  <br />
                </>
              )}
              {t("assignDialog.note")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Label htmlFor="company-select">{t("assignDialog.targetCompany")}</Label>
            <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
              <SelectTrigger id="company-select">
                <span className="flex items-center gap-2 overflow-hidden">
                  {selectedCompanyId && <Building size={14} className="shrink-0 text-muted-foreground" />}
                  <SelectValue placeholder={t("assignDialog.selectCompany")} />
                </span>
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    <span className="flex flex-col">
                      <span>{company.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {displayOrganizationNumber(company.organizationNumber)}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setEmailToAssign(null);
                setSelectedCompanyId("");
              }}
              disabled={isAssigning}
            >
              {tc("cancel")}
            </Button>
            <Button onClick={handleAssign} disabled={!selectedCompanyId || isAssigning}>
              {isAssigning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("assignDialog.assigning")}
                </>
              ) : (
                t("assign")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dismiss confirmation */}
      {/* AlertDialogAction auto-closes on click; use a plain Button so the dialog stays
          open during the async dismiss and shows the spinner on failure. */}
      <AlertDialog open={!!emailToDismiss} onOpenChange={(open) => !open && !isDismissing && setEmailToDismiss(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dismissDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.rich("dismissDialog.body", {
                sender: emailToDismiss?.sender_email ?? "",
                mono: (chunks) => <span className="font-mono text-xs">{chunks}</span>,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDismissing}>{tc("cancel")}</AlertDialogCancel>
            <Button onClick={handleDismiss} disabled={isDismissing} variant="destructive">
              {isDismissing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("dismissDialog.dismissing")}
                </>
              ) : (
                t("dismiss")
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
